<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, ref, type ComputedRef, type Ref } from "vue";
import type { DateTimeFormatPreference } from "../api/user-settings.js";
import { formatDateTime } from "../formatting/date-time.js";
import TimePicker from "./TimePicker.vue";

interface Props {
  readonly modelValue: string;
  readonly dateTimeFormat: DateTimeFormatPreference;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
}

const props: Props = withDefaults(defineProps<Props>(), { disabled: false });
const emit: (event: "update:modelValue", value: string) => void = defineEmits<{
  (event: "update:modelValue", value: string): void;
}>();

const open: Ref<boolean> = ref(false);
const calendarMonthOffset: Ref<number> = ref(0);
const localDate: ComputedRef<Date> = computed((): Date => new Date(props.modelValue));
const dateValue: ComputedRef<string> = computed((): string => {
  const date: Date = localDate.value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
});
const timeValue: ComputedRef<string> = computed((): string => {
  const date: Date = localDate.value;
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
});
const twelveHour: ComputedRef<boolean> = computed((): boolean => props.dateTimeFormat === "us_12h"
  || (props.dateTimeFormat === "browser" && new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).resolvedOptions().hour12 === true));
const displayValue: ComputedRef<string> = computed((): string => formatDateTime(props.modelValue, props.dateTimeFormat));

function emitLocalDateTime(nextDate: string, nextTime: string): void {
  const parsed: Date = new Date(`${nextDate}T${nextTime}:00`);
  if (!Number.isNaN(parsed.getTime())) {
    emit("update:modelValue", parsed.toISOString());
  }
}

interface CalendarDay {
  readonly value: string | null;
  readonly label: string;
}

const calendarMonth: ComputedRef<Date> = computed((): Date => new Date(
  localDate.value.getFullYear(),
  localDate.value.getMonth() + calendarMonthOffset.value,
  1,
));
const calendarMonthLabel: ComputedRef<string> = computed((): string => new Intl.DateTimeFormat(
  props.dateTimeFormat === "de_24h" ? "de-DE" : props.dateTimeFormat === "us_12h" ? "en-US" : undefined,
  { month: "long", year: "numeric" },
).format(calendarMonth.value));
const calendarDays: ComputedRef<readonly CalendarDay[]> = computed((): readonly CalendarDay[] => {
  const month: Date = calendarMonth.value;
  const firstWeekday: number = (month.getDay() + 6) % 7;
  const daysInMonth: number = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const days: CalendarDay[] = [];
  for (let index: number = 0; index < firstWeekday; index += 1) days.push({ value: null, label: "" });
  for (let day: number = 1; day <= daysInMonth; day += 1) {
    days.push({ value: formatIsoDate(new Date(month.getFullYear(), month.getMonth(), day)), label: String(day) });
  }
  while (days.length % 7 !== 0) days.push({ value: null, label: "" });
  return days;
});

function formatIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function selectDate(value: string): void {
  emitLocalDateTime(value, timeValue.value);
  calendarMonthOffset.value = 0;
}

function updateTime(value: string): void {
  emitLocalDateTime(dateValue.value, value);
}

function toggle(): void {
  if (!props.disabled) {
    open.value = !open.value;
    if (open.value) calendarMonthOffset.value = 0;
  }
}
</script>

<template>
  <div class="date-time-picker">
    <button class="date-time-picker__trigger" type="button" :aria-label="ariaLabel" :aria-expanded="open" aria-haspopup="dialog" :disabled="disabled" @click="toggle">
      <span>{{ displayValue }}</span><span aria-hidden="true">⌄</span>
    </button>
    <div v-if="open" class="date-time-picker__popover" role="dialog" :aria-label="`${ariaLabel} picker`" @keydown.escape="open = false">
      <div class="date-time-picker__selected-date">{{ displayValue }}</div>
      <div class="date-time-picker__calendar" :aria-label="`${ariaLabel} calendar`">
        <div class="date-time-picker__calendar-heading">
          <button class="button button--quiet button--compact" type="button" aria-label="Previous month" @click="calendarMonthOffset -= 1">‹</button>
          <strong>{{ calendarMonthLabel }}</strong>
          <button class="button button--quiet button--compact" type="button" aria-label="Next month" @click="calendarMonthOffset += 1">›</button>
        </div>
        <div class="date-time-picker__weekdays" aria-hidden="true"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div>
        <div class="date-time-picker__days">
          <span v-for="(day, index) in calendarDays" :key="`${day.value ?? 'empty'}-${index}`" class="date-time-picker__day-slot">
            <button v-if="day.value !== null" class="date-time-picker__day" :class="{ 'date-time-picker__day--selected': day.value === dateValue }" type="button" :aria-label="day.value" @click="selectDate(day.value)">{{ day.label }}</button>
          </span>
        </div>
      </div>
      <TimePicker :model-value="timeValue" :twelve-hour="twelveHour" :ariaLabel="`${ariaLabel} time`" @update:model-value="updateTime" />
      <button class="button button--quiet button--compact" type="button" @click="open = false">Done</button>
    </div>
  </div>
</template>

<style scoped>
.date-time-picker { position: relative; min-width: 0; }
.date-time-picker__trigger { display: flex; width: 100%; min-height: 42px; align-items: center; justify-content: space-between; gap: 0.5rem; border: 1px solid var(--border); border-radius: 8px; padding: 0.55rem 0.7rem; color: var(--text); background: rgba(7, 17, 31, 0.65); font: inherit; text-align: left; cursor: pointer; }
.date-time-picker__trigger:hover:not(:disabled), .date-time-picker__trigger:focus-visible:not(:disabled) { border-color: rgba(55, 215, 160, 0.6); outline: none; }
.date-time-picker__trigger:disabled { cursor: not-allowed; color: var(--muted); background: rgba(7, 17, 31, 0.36); opacity: 0.62; }
.date-time-picker__popover { position: absolute; z-index: 30; top: calc(100% + 0.35rem); right: 0; display: grid; min-width: 18rem; gap: 0.5rem; padding: 0.6rem; border: 1px solid var(--border); border-radius: 8px; background: #0b1a2c; box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35); }
.date-time-picker__selected-date { padding: 0.2rem 0.15rem; color: var(--muted); font-size: 0.82rem; }
.date-time-picker__calendar { display: grid; gap: 0.35rem; }
.date-time-picker__calendar-heading { display: grid; grid-template-columns: 2.4rem 1fr 2.4rem; align-items: center; text-align: center; }
.date-time-picker__weekdays, .date-time-picker__days { display: grid; grid-template-columns: repeat(7, 1fr); gap: 0.18rem; }
.date-time-picker__weekdays { color: var(--muted); font-size: 0.68rem; text-align: center; }
.date-time-picker__day-slot { display: grid; min-height: 1.9rem; }
.date-time-picker__day { border: 0; border-radius: 5px; color: var(--text); background: transparent; font: inherit; cursor: pointer; }
.date-time-picker__day:hover, .date-time-picker__day:focus-visible { background: rgba(55, 215, 160, 0.16); outline: none; }
.date-time-picker__day--selected { color: #07111f; background: var(--accent); font-weight: 750; }
</style>
